import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { LOP30_PROGRAM_SLUG, LOP4UKER_PROGRAM_SLUG } from '../../utils/programDeepLinks';
import { APP_STORE_URL, PLAY_STORE_URL, tryOpenAppThenStore } from '../../utils/appStoreLinks';
import { appendUtmToUrl, getStoredUtmParams } from '../../utils/utmTracking';
import HomeMarketingLayout, {
  createHomeAccountUrl
} from '../../components/layout/HomeMarketingLayout';
import QuoteTestimonials from '../../components/common/QuoteTestimonials';
import './Home.css';

const SPOTIFY_SHOW_URL = 'https://open.spotify.com/show/2FbsnJU03WoIsViXBx97st';
const ACAST_EMBED_URL = 'https://embed.acast.com/68a739eae2f63983a7306bea?feed=true';
const ACTIVITY_TEASER_SECONDS = 10;

const ACTIVITY_TREATS = [
  { id: 34, fallbackName: '20 x 20 sek-intervall' },
  { id: 151, fallbackName: '45/15-intervall' },
  { id: 59, fallbackName: 'Gå/løpeøkt – 30 min' },
  { id: 54, fallbackName: '60 min løpetur – 165 spm' }
];

const START_CARDS = [
  {
    title: 'Begynne å løpe',
    image: '/img/hvorvildustarte/Hvorvildustarte4.png',
    href: `/program/${LOP4UKER_PROGRAM_SLUG}`
  },
  {
    title: 'Komme i løpeform',
    image: '/img/hvorvildustarte/Hvorvildustarte3.png',
    href: '/programs/47'
  },
  {
    title: 'Løpe lenger',
    image: '/img/hvorvildustarte/Hvorvildustarte2.png',
    href: '/programs/48'
  },
  {
    title: 'Øke farten',
    image: '/img/hvorvildustarte/Hvorvildustarte1.png',
    href: '/programs/51'
  }
];

const APP_STEPS = [
  {
    n: 1,
    title: 'Last ned appen',
    text: 'Hent Løpeglad gratis i App Store eller Google Play.',
    image: '/img/app/home.png',
    showStores: true
  },
  {
    n: 2,
    title: 'Velg nivå',
    text: 'Finn nivået som passer deg – fra nybegynner til erfaren.',
    image: '/img/app/level.png'
  },
  {
    n: 3,
    title: 'Få anbefalte økter og program',
    text: 'Vi foreslår økter og programmer basert på målet ditt.',
    image: '/img/app/home-weekly.png'
  },
  {
    n: 4,
    title: 'Velg dine favoritter',
    text: 'Lagre det du liker og bygg dine egne spillelister.',
    image: '/img/app/favorites.png'
  }
];

const createAccountUrl = createHomeAccountUrl;

const activityTeaserUrl = (id) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}/teaser?type=audio`;

const activityHasAudio = (data) =>
  Boolean(
    data?.hasAudioTeaser ||
      data?.norwegianURL ||
      data?.norwegianUrl ||
      data?.englishURL ||
      data?.englishUrl
  );

const Waveform = () => (
  <svg className="home-wave" viewBox="0 0 120 28" aria-hidden="true">
    {[4, 10, 16, 8, 20, 12, 22, 9, 18, 6, 14, 21, 11, 17, 7, 19, 13, 23, 8, 15].map((h, i) => (
      <rect
        key={i}
        x={i * 6}
        y={(28 - h) / 2}
        width="3"
        height={h}
        rx="1.5"
        fill="currentColor"
      />
    ))}
  </svg>
);

const Home = () => {
  const [treats, setTreats] = useState([]);
  const [treatsLoading, setTreatsLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [selectedInterval, setSelectedInterval] = useState('year');
  const [weeklyExercise, setWeeklyExercise] = useState(null);
  const audioRefs = useRef({});

  useEffect(() => {
    let cancelled = false;

    const fetchTreats = async () => {
      setTreatsLoading(true);
      try {
        const results = await Promise.all(
          ACTIVITY_TREATS.map(async ({ id, fallbackName }) => {
            try {
              const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}`
              );
              return {
                id,
                name: data?.name || fallbackName,
                shortDescription:
                  data?.short_description ||
                  data?.shortDescription ||
                  'Her kommer Elins beskrivelse av økten.',
                audioUrl: activityHasAudio(data) ? activityTeaserUrl(id) : null
              };
            } catch (err) {
              console.error(`Could not load activity treat ${id}:`, err);
              return {
                id,
                name: fallbackName,
                shortDescription: 'Her kommer Elins beskrivelse av økten.',
                audioUrl: null
              };
            }
          })
        );
        if (!cancelled) setTreats(results);
      } finally {
        if (!cancelled) setTreatsLoading(false);
      }
    };

    const fetchPricingPlans = async () => {
      setPricingLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans`,
          { withCredentials: true }
        );
        // Same filter as IndividualUser.js
        const plansWithTiers = (response.data || []).filter(
          (plan) => plan.tiers && plan.tiers.length > 0
        );
        if (!cancelled) setPricingPlans(plansWithTiers);
      } catch (err) {
        console.error('Could not load pricing plans:', err);
        if (!cancelled) setPricingPlans([]);
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    };

    const fetchWeekly = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/weekly-exercises/current`,
          { credentials: 'include' }
        );
        if (!response.ok) throw new Error('Failed');
        const data = await response.json();
        if (!cancelled) setWeeklyExercise(data);
      } catch (_) {
        if (!cancelled) setWeeklyExercise(null);
      }
    };

    fetchTreats();
    fetchPricingPlans();
    fetchWeekly();

    return () => {
      cancelled = true;
    };
  }, []);

  const trialUrl = createAccountUrl();

  const toggleTreat = (id) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    Object.entries(audioRefs.current).forEach(([otherId, el]) => {
      if (Number(otherId) !== id && el && !el.paused) {
        el.pause();
        el.currentTime = 0;
      }
    });

    if (playingId === id && !audio.paused) {
      audio.pause();
      setPlayingId(null);
      return;
    }

    audio.play().catch(() => {});
    setPlayingId(id);
  };

  const handleTreatTimeUpdate = (id) => {
    const audio = audioRefs.current[id];
    if (!audio) return;
    if (audio.currentTime >= ACTIVITY_TEASER_SECONDS) {
      audio.pause();
      audio.currentTime = 0;
      setPlayingId(null);
    }
  };

  const handleTreatSeeked = (id) => {
    const audio = audioRefs.current[id];
    if (!audio) return;
    if (audio.currentTime > ACTIVITY_TEASER_SECONDS) {
      audio.currentTime = 0;
      audio.pause();
      setPlayingId(null);
    }
  };

  const getTierForInterval = (plan, interval) => {
    if (!plan?.tiers?.length) return null;
    if (interval === 'month') {
      return plan.tiers.find(
        (tier) =>
          tier.payment_interval === 'month' && Number(tier.interval_count) === 1
      );
    }
    if (interval === 'month-6') {
      return plan.tiers.find(
        (tier) =>
          tier.payment_interval === 'month' && Number(tier.interval_count) === 6
      );
    }
    return plan.tiers.find(
      (tier) =>
        tier.payment_interval === 'year' && Number(tier.interval_count) === 1
    );
  };

  const getMonthlyEquivalent = (tier, interval) => {
    if (!tier) return null;
    const amount = parseFloat(tier.amount);
    if (Number.isNaN(amount)) return null;
    if (interval === 'month') return Math.round(amount);
    if (interval === 'month-6') return Math.round(amount / 6);
    return Math.round(amount / 12);
  };

  const getSavingsPercent = (plan, interval) => {
    if (interval === 'month') return null;
    const monthlyTier = getTierForInterval(plan, 'month');
    const selectedTier = getTierForInterval(plan, interval);
    if (!monthlyTier || !selectedTier) return null;
    const regular = parseFloat(monthlyTier.amount);
    const divisor = interval === 'month-6' ? 6 : 12;
    const currentMonthly = parseFloat(selectedTier.amount) / divisor;
    if (!(regular > 0) || !(currentMonthly > 0)) return null;
    const pct = Math.round(((regular - currentMonthly) / regular) * 100);
    return pct > 0 ? pct : null;
  };

  const intervalTierLabel = (planName) => {
    if (selectedInterval === 'year') return `${planName} - yearly`;
    if (selectedInterval === 'month-6') return `${planName} - 6 months`;
    return `${planName} - monthly`;
  };

  const intervalPeriodLabel =
    selectedInterval === 'year'
      ? 'Ved 12 måneders abonnement'
      : selectedInterval === 'month-6'
        ? 'Ved 6 måneders abonnement'
        : 'Ved månedlig abonnement';

  const planCheckoutUrl = (plan, tier) =>
    createAccountUrl({
      priceTierId: tier.id,
      productName: plan.name,
      tierLabel: intervalTierLabel(plan.name),
      priceAmount: tier.amount
    });

  const openWeeklyInApp = () => {
    const targetId = weeklyExercise?.activity_idactivity || weeklyExercise?.idactivity;
    if (!targetId) {
      tryOpenAppThenStore(appendUtmToUrl('lopeglad://', getStoredUtmParams()));
      return;
    }
    tryOpenAppThenStore(
      appendUtmToUrl(`lopeglad://weekly-exercise/${targetId}`, getStoredUtmParams())
    );
  };

  // Same tiers_mode filter as IndividualUser renderPricingTiers
  const tieredPlans = pricingPlans.filter((plan) => Number(plan.tiers_mode) === 1);
  const sortedPlans = [...tieredPlans].sort((a, b) => {
    const aFull = a.name.toLowerCase().includes('full');
    const bFull = b.name.toLowerCase().includes('full');
    if (aFull === bFull) return 0;
    return aFull ? -1 : 1;
  });

  return (
    <HomeMarketingLayout>
      <section className="home-hero">
        <div className="home-hero-bg" aria-hidden="true">
          <img src="/img/background image.jpg" alt="" />
        </div>
        <div className="home-hero-scrim" aria-hidden="true" />
        <div className="home-container home-hero-grid">
          <div className="home-hero-copy">
            <h1>Norsk løpeapp med menneskelig coaching</h1>
            <p className="home-hero-lead">
              Løp lettere – med rytme, pust og en trener på øret
            </p>
            <p className="home-hero-text">
              Guidede økter med musikk og norsk stemme som hjelper deg å bygge mestring,
              kontinuitet og løpeglede – i ditt tempo.
            </p>
            <div className="home-hero-actions">
              <Link
                to={`/program/${LOP30_PROGRAM_SLUG}`}
                className="home-btn home-btn--primary"
              >
                Løp 30 min om 12 uker
              </Link>
              <Link to={trialUrl} className="home-btn home-btn--ghost">
                Start gratis i 7 dager
              </Link>
            </div>
          </div>
          <div className="home-hero-visual" aria-hidden="true">
            <img className="home-hero-phone home-hero-phone--back" src="/img/app/collections.png" alt="" />
            <img className="home-hero-phone home-hero-phone--front" src="/img/app/home.png" alt="" />
          </div>
        </div>
      </section>

      <aside className="home-promo-banner" aria-label="Gratis kampanje">
        <div className="home-container">
          <Link
            to={`/program/${LOP4UKER_PROGRAM_SLUG}`}
            className="home-promo-banner__link"
          >
            <span className="home-promo-banner__badge">Gratis</span>
            <div className="home-promo-banner__copy">
              <strong>Kom i gang på 4 uker – gratis 7. okt – 15. nov 2026</strong>
              <span>
                Trygt steg-for-steg-program for deg som vil begynne å løpe. Tilbudet gjelder
                i perioden 07.10–15.11.26.
              </span>
            </div>
            <span className="home-promo-banner__cta">
              Se programmet
              <i className="fas fa-arrow-right" aria-hidden="true" />
            </span>
          </Link>
        </div>
      </aside>

      <section className="home-section" id="programmer">
        <div className="home-container">
          <h2 className="home-h2">Hvordan vil du bruke Løpeglad?</h2>
          <div className="home-use-grid">
            <article
              className="home-use-card"
              style={{ backgroundImage: "url('/img/background image.jpg')" }}
            >
              <div className="home-use-body">
                <span className="home-use-label">Av og til</span>
                <h3>Velg en økt som passer i dag</h3>
                <p>
                  Fri tilgang til Øktbiblioteket med løping, styrke, pust og mindfulness.
                </p>
                <a href="/albums" className="home-btn home-btn--primary home-btn--sm">
                  Se Øktbiblioteket <i className="fas fa-arrow-right" aria-hidden="true" />
                </a>
              </div>
            </article>
            <article
              className="home-use-card"
              style={{ backgroundImage: "url('/img/running1.jpeg')" }}
            >
              <div className="home-use-body">
                <span className="home-use-label">Full tilgang</span>
                <h3>Følg et program mot et mål</h3>
                <p>
                  Få en tydelig plan fra uke til uke, med økter som bygger deg gradvis videre.
                </p>
                <Link
                  to={`/programs`}
                  className="home-btn home-btn--primary home-btn--sm"
                >
                  Finn programmet ditt <i className="fas fa-arrow-right" aria-hidden="true" />
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="home-section home-section--soft">
        <div className="home-container">
          <h2 className="home-h2">Hvor vil du starte?</h2>
          <div className="home-start-grid">
            {START_CARDS.map((card) => {
              const inner = (
                <>
                  <img src={card.image} alt="" />
                  <div className="home-start-overlay">
                    <span>{card.title}</span>
                    <i className="fas fa-arrow-right" aria-hidden="true" />
                  </div>
                </>
              );
              if (card.href.startsWith('#')) {
                return (
                  <a key={card.title} href={card.href} className="home-start-card">
                    {inner}
                  </a>
                );
              }
              return (
                <Link key={card.title} to={card.href} className="home-start-card">
                  {inner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="home-section" id="gratisokt">
        <div className="home-container">
          <div className="home-weekly">
            <div className="home-weekly-arcs" aria-hidden="true">
              <span className="home-weekly-arc home-weekly-arc--light" />
              <span className="home-weekly-arc home-weekly-arc--dark" />
            </div>
            <div className="home-weekly-player">
              <h3 className="home-weekly-player-title">
                {weeklyExercise?.name ||
                  weeklyExercise?.activityName ||
                  'Økt 2 mot bedre form'}
              </h3>
              <p className="home-weekly-player-sub">
                {weeklyExercise?.short_description ||
                  weeklyExercise?.activityShortDescription ||
                  (weeklyExercise?.minutes
                    ? `${weeklyExercise.minutes} min`
                    : 'Guidet økt med musikk og coaching')}
              </p>
              <div className="home-weekly-progress">
                <div className="home-weekly-progress-track">
                  <span className="home-weekly-progress-knob" />
                </div>
                <div className="home-weekly-progress-times">
                  <span>00:00</span>
                  <span>
                    {weeklyExercise?.minutes
                      ? `${String(weeklyExercise.minutes).padStart(2, '0')}:00`
                      : '20:00'}
                  </span>
                </div>
              </div>
              <div className="home-weekly-controls">
                <span className="home-weekly-skip">
                  <i className="fas fa-undo" />
                  <em>5</em>
                </span>
                <button
                  type="button"
                  className="home-weekly-play"
                  onClick={openWeeklyInApp}
                  aria-label="Åpne ukens gratisøkt i appen"
                >
                  <i className="fas fa-play" />
                </button>
                <span className="home-weekly-skip home-weekly-skip--fwd">
                  <i className="fas fa-redo" />
                  <em>5</em>
                </span>
              </div>
            </div>

            <div className="home-weekly-info">
              <span className="home-weekly-badge">Gratis denne uken</span>
              <h2>Ukens gratisøkt</h2>
              <p>
                En komplett økt med coaching, musikk og motivasjon. Åpne den i
                appen – helt gratis.
              </p>
              <button
                type="button"
                className="home-btn home-btn--primary"
                onClick={openWeeklyInApp}
              >
                Åpne gratisøkten i appen
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section" id="hjelp">
        <div className="home-container">
          <Link to="/hva-hjelper-vi-deg-med" className="home-help-teaser">
            <div className="home-help-teaser-media">
              <img src="/img/hvakanvihjelpdegmed.jpg" alt="" />
            </div>
            <div className="home-help-teaser-copy">
              <h2 className="home-h2">Hva hjelper vi deg med?</h2>
              <p>
                Fra å komme seg ut døra, til å holde igjen når det blir tungt –
                Løpeglad er med deg gjennom hele turen.
              </p>
              <span className="home-help-teaser-cta">
                Les mer
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </span>
            </div>
          </Link>
        </div>
      </section>

<section className="home-section" id="okter">
        <div className="home-container">
          <h2 className="home-h2">Prøv en av våre mest populære økter</h2>
          <p className="home-lead">
            Lytt til en smakebit og finn økten som passer deg i dag.
          </p>
          {treatsLoading ? (
            <p className="home-muted">Laster smakebiter…</p>
          ) : (
            <div className="home-treats-grid">
              {treats.map((treat) => (
                <article key={treat.id} className="home-treat-card">
                  <h3>{treat.name}</h3>
                  <p>{treat.shortDescription}</p>
                  <button
                    type="button"
                    className="home-treat-play"
                    onClick={() => toggleTreat(treat.id)}
                    disabled={!treat.audioUrl}
                    aria-label={
                      playingId === treat.id ? `Pause ${treat.name}` : `Spill ${treat.name}`
                    }
                  >
                    <span className="home-treat-play-btn">
                      <i
                        className={`fas ${playingId === treat.id ? 'fa-pause' : 'fa-play'}`}
                        aria-hidden="true"
                      />
                    </span>
                    <Waveform />
                    <span className="home-treat-play-label">
                      Lytt til smakebit
                      <i className="fas fa-arrow-right" aria-hidden="true" />
                    </span>
                  </button>
                  {treat.audioUrl && (
                    <audio
                      ref={(el) => {
                        if (el) audioRefs.current[treat.id] = el;
                      }}
                      src={treat.audioUrl}
                      preload="none"
                      onTimeUpdate={() => handleTreatTimeUpdate(treat.id)}
                      onSeeked={() => handleTreatSeeked(treat.id)}
                      onEnded={() => setPlayingId(null)}
                      onPause={() => {
                        if (playingId === treat.id) setPlayingId(null);
                      }}
                    />
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="home-section home-section--soft" id="appen">
        <div className="home-container">
          <h2 className="home-h2">Slik fungerer appen</h2>
          <div className="home-steps-grid">
            {APP_STEPS.map((step) => (
              <article key={step.n} className="home-step-card">
                <div className="home-step-phone">
                  <img src={step.image} alt="" />
                  <span className="home-step-num">{step.n}</span>
                </div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
                {step.showStores && (
                  <div className="home-stores">
                    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                      <img src="/img/AppStore.png" alt="Last ned på App Store" />
                    </a>
                    <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                      <img src="/img/GooglePlayNorwegian.png" alt="Last ned på Google Play" />
                    </a>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

            <section className="home-section home-section--soft home-video-wrap">
        <div className="home-container">
          <h2 className="home-h2">Ikke la stresset løpe fra deg</h2>
          <div className="home-video-frame">
            <video controls playsInline preload="metadata" aria-label="Løpeglad kampanjevideo">
              <source src="/video/lopeglad_hostkampanje_2026_4K.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      <section className="home-section">
        <div className="home-container">
          <QuoteTestimonials titleClassName="home-h2 home-h2--center" />
        </div>
      </section>

      <section className="home-section home-section--soft" id="pris">
        <div className="home-container">
          <h2 className="home-h2 home-h2--center">Velg abonnement</h2>
          <div className="home-interval" role="group" aria-label="Betalingsintervall">
            <button
              type="button"
              className={selectedInterval === 'month' ? 'is-active' : ''}
              onClick={() => setSelectedInterval('month')}
            >
              Mnd
            </button>
            <button
              type="button"
              className={selectedInterval === 'month-6' ? 'is-active' : ''}
              onClick={() => setSelectedInterval('month-6')}
            >
              6 mnd
            </button>
            <button
              type="button"
              className={selectedInterval === 'year' ? 'is-active' : ''}
              onClick={() => setSelectedInterval('year')}
            >
              Årlig
            </button>
          </div>

          {pricingLoading ? (
            <p className="home-muted home-center">Laster priser…</p>
          ) : sortedPlans.length === 0 ? (
            <p className="home-muted home-center">Priser er midlertidig utilgjengelige.</p>
          ) : (
            <div className="home-pricing-grid">
              {sortedPlans.map((plan) => {
                const tier = getTierForInterval(plan, selectedInterval);
                if (!tier) return null;
                const monthlyEq = getMonthlyEquivalent(tier, selectedInterval);
                const savings = getSavingsPercent(plan, selectedInterval);
                const isFull = plan.name.toLowerCase().includes('full tilgang');

                return (
                  <article
                    key={plan.idpricing}
                    className={`home-price-card${isFull ? ' is-selected' : ''}`}
                  >
                    {isFull && (
                      <span className="home-price-check" aria-hidden="true">
                        <i className="fas fa-check" />
                      </span>
                    )}
                    {savings != null && (
                      <span className="home-price-save">Spar {savings}%</span>
                    )}
                    <h3>{plan.name}</h3>
                    <p className="home-price-amount">
                      Fra <strong>{monthlyEq}</strong> kr/mnd
                    </p>
                    <p className="home-price-period">
                      {intervalPeriodLabel}
                    </p>
                    <ul>
                      <li><i className="fas fa-check" aria-hidden="true" /> Hele øktbiblioteket</li>
                      <li><i className="fas fa-check" aria-hidden="true" /> Egne spillelister</li>
                      <li><i className="fas fa-check" aria-hidden="true" /> Favoritter og nivå</li>
                      {isFull ? (
                        <li><i className="fas fa-check" aria-hidden="true" /> <strong>Alle programmer</strong></li>
                   
                      ) : (
                        <li><i className="fas fa-check" aria-hidden="true" /> Enkeltøkter etter dagsform</li>
                      )}
                    </ul>
                    <Link
                      to={planCheckoutUrl(plan, tier)}
                      className={`home-btn ${isFull ? 'home-btn--primary' : 'home-btn--ghost'} home-btn--block`}
                    >
                      Velg {plan.name}
                    </Link>
                  </article>
                );
              })}
            </div>
          )}
          <p className="home-pricing-note">
            Velg fritt fra biblioteket – eller følg en plan mot målet ditt.
          </p>
        </div>
      </section>

      <section className="home-section" id="podcast">
        <div className="home-container home-podcast">
          <div className="home-podcast-intro">
            <img src="/img/Podkast-02.png" alt="" className="home-podcast-art" />
            <h2 className="home-h2">Løpeglad på øret</h2>
            <p className="home-lead">Inspirasjon, kunnskap og løpeprat</p>
            <a
              href={SPOTIFY_SHOW_URL}
              className="home-btn home-btn--primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-spotify" aria-hidden="true" />
              Lytt på Spotify
            </a>
          </div>
          <div className="home-podcast-episodes">
            <h3>Siste episoder</h3>
            <div className="home-acast">
              <iframe
                src={ACAST_EMBED_URL}
                title="Løpeglad Podkast"
                width="100%"
                height="280"
                frameBorder="0"
                allow="autoplay"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="home-section home-section--soft" id="om-oss">
        <div className="home-container">
          <h2 className="home-h2">Menneskene du hører på øret</h2>
          <div className="home-team-grid">
            <article className="home-team-card">
              <img src="/img/elin.png" alt="Elin" />
              <div>
                <h3>Elin</h3>
                <p className="home-team-role">Psykomotorisk fysioterapeut og coach</p>
                <p>
                  Elin guider deg gjennom øktene med rolig, tydelig coaching – fokus, rytme og
                  mestring i fokuspunkt.
                </p>
              </div>
            </article>
            <article className="home-team-card">
              <img src="/img/teamet_Larserik.png" alt="Lars-Erik" />
              <div>
                <h3>Lars-Erik</h3>
                <p className="home-team-role">Musikk og produksjon</p>
                <p>
                  Lars-Erik bygger lydbildet rundt øktene – musikk tilpasset frekvens og flyt, så
                  løpingen føles lettere.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="home-cta-banner">
        <div className="home-cta-banner-bg" aria-hidden="true">
          <img src="/img/program/bakkestranda2.jpg" alt="" />
        </div>
        <div className="home-container home-cta-banner-inner">
          <h2>Klar for å finne din løpeglede?</h2>
          <p>Start reisen i dag – vi er med deg hele veien.</p>
          <Link
            id="home-cta-banner-btn"
            to={trialUrl}
            className="home-btn home-btn--light"
          >
            Start gratis i 7 dager
          </Link>
        </div>
      </section>

    </HomeMarketingLayout>
  );
};

export default Home;